import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import Typography from "../../../../components/Typography/Typography";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { Button } from "../../../../components/Buttons";
import IconComponent from "../../../../components/Icon/IconComponent";
import { postRequest } from "../../../../Apis/apiWrapper";
import { paymentEndpoints } from "../../../../utils/constants/httpConstants";
import {
  BRAND,
  GREY,
  BASE,
  SUCCESS,
  BORDER,
} from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { EMITTER_ERROR } from "../../../../utils/constants";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { PROFILE_PATH } from "../../../../utils/constants/routes";

const BUY_PACKAGE_TAB_PATH = `${PROFILE_PATH}?tab=BuyPackage`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(false);

  const styles = {
    container: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      background: BASE.BACKGROUND,
      padding: "24px",
    },
    card: {
      background: BASE.WHITE,
      borderRadius: "12px",
      padding: "48px",
      maxWidth: "500px",
      width: "100%",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
      border: `1px solid ${BORDER.DEFAULT}`,
    },
    iconWrapper: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      background: SUCCESS.W_50,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    successIcon: {
      fontSize: "40px",
      color: SUCCESS.W_500,
    },
    errorIconWrapper: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      background: "#FEF2F2",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    errorIcon: {
      fontSize: "40px",
      color: BRAND.PRIMARY,
    },
    detailsBox: {
      background: BASE.BACKGROUND,
      borderRadius: "8px",
      padding: "16px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    detailRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      width: "100%",
      marginTop: "8px",
    },
  };

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setVerifying(false);
      setError(true);
    }
  }, []);

  const verifyPayment = async (sessionId) => {
    try {
      setVerifying(true);
      const response = await postRequest(
        navigate,
        paymentEndpoints.verifySession,
        { session_id: sessionId }
      );

      if (response?.data?.status) {
        setPaymentDetails(response.data.data);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error("Error verifying payment:", err);
      setError(true);
      EventEmitter.emit(
        EMITTER_ERROR,
        err?.data?.message || "Failed to verify payment."
      );
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <Box style={styles.container}>
        <Box style={styles.card}>
          <Typography
            size={"lg"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label="Verifying your payment..."
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label="Please wait while we confirm your payment."
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box style={styles.container}>
        <Box style={styles.card}>
          <Box style={styles.errorIconWrapper}>
            <IconComponent
              iconLabel={ICON_LABELS.ERROR}
              style={styles.errorIcon}
            />
          </Box>
          <Typography
            size={"lg"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label="Payment Verification Failed"
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label="We couldn't verify your payment. If you were charged, please contact support."
          />
          <Box style={styles.buttonGroup}>
            <Button
              label="Go to Settings"
              size={"md40"}
              fullWidth
              variant="primary"
              onClick={() => navigate(BUY_PACKAGE_TAB_PATH)}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box style={styles.container}>
      <Box style={styles.card}>
        <Box style={styles.iconWrapper}>
          <IconComponent
            iconLabel={ICON_LABELS.CHECK_CIRCLE}
            style={styles.successIcon}
          />
        </Box>
        <Typography
          size={"lg"}
          color={GREY.PRIMARY}
          fontWeight={fontWeight.BOLD}
          label="Payment Successful!"
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label="Your subscription is now active and credits have been added to your account."
        />

        {paymentDetails && (
          <Box style={styles.detailsBox}>
            {paymentDetails.payment_status && (
              <Box style={styles.detailRow}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label="Status"
                />
                <TypographyInline
                  size={"sm"}
                  color={SUCCESS.W_500}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={paymentDetails.payment_status === "paid" ? "Paid" : paymentDetails.payment_status}
                />
              </Box>
            )}
            {paymentDetails.amount_total != null && (
              <Box style={styles.detailRow}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label="Amount"
                />
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={`$${(paymentDetails.amount_total / 100).toFixed(2)}`}
                />
              </Box>
            )}
            {paymentDetails.customer_email && (
              <Box style={styles.detailRow}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label="Email"
                />
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={paymentDetails.customer_email}
                />
              </Box>
            )}
          </Box>
        )}

        <Box style={styles.buttonGroup}>
          <Button
            label="Go to Settings"
            size={"md40"}
            fullWidth
            variant="primary"
            onClick={() => navigate(BUY_PACKAGE_TAB_PATH)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentSuccess;
